import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BewerberStatus } from '../types/bewerber';

export type ViewMode = 'table' | 'kanban';

export interface BewerberFilters {
  search: string;
  status: BewerberStatus | '';
  ortStelle: string;
  assignedUserId: string;
  view: ViewMode;
}

export function useBewerberFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse filters from URL
  const filters = useMemo<BewerberFilters>(
    () => ({
      search: searchParams.get('search') || '',
      status: (searchParams.get('status') as BewerberStatus) || '',
      ortStelle: searchParams.get('ortStelle') || '',
      assignedUserId: searchParams.get('assignedUserId') || '',
      view: searchParams.get('view') === 'table' ? 'table' : 'kanban',
    }),
    [searchParams]
  );

  const setFilter = useCallback(
    <K extends keyof BewerberFilters>(key: K, value: BewerberFilters[K]) => {
      setSearchParams(
        (prev) => {
          if (value) {
            prev.set(key, value as string);
          } else {
            prev.delete(key);
          }
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        prev.delete('search');
        prev.delete('status');
        prev.delete('ortStelle');
        prev.delete('assignedUserId');
        // Keep view mode
        return prev;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const hasActiveFilters = useMemo(
    () => Boolean(filters.search || filters.status || filters.ortStelle || filters.assignedUserId),
    [filters]
  );

  return {
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
  };
}
